import {
  PROJECT_PHASES,
  PROJECT_RISK_LEVELS,
  PROJECT_STATUSES,
  type ProjectFilters,
  type ProjectPortfolioFilters,
  type ProjectPhase,
  type ProjectRiskLevel,
  type ProjectSortField,
  type ProjectStatus,
  type SortOrder,
} from './projects.types';

export type ProjectView = 'dashboard' | 'table' | 'kanban';

export const DEFAULT_PROJECT_FILTERS: ProjectFilters = {
  search: '',
  status: '',
  phase: '',
  riskLevel: '',
  sector: '',
  projectId: '',
  regionId: '',
  page: 1,
  pageSize: 25,
  sortBy: 'updatedAt',
  sortOrder: 'desc',
};

const SORT_FIELDS: ProjectSortField[] = [
  'name',
  'projectNumber',
  'healthScore',
  'contractValue',
  'updatedAt',
  'startDate',
  'substantialCompletion',
];

const PROJECT_FILTER_KEYS = [
  'search',
  'status',
  'phase',
  'riskLevel',
  'sector',
  'projectId',
  'regionId',
  'cohort',
  'page',
  'sortBy',
  'sortOrder',
  'view',
] as const;

function included<T extends string>(values: readonly T[], value: string | null): value is T {
  return value !== null && values.includes(value as T);
}

export function projectFiltersFromParams(params: URLSearchParams): ProjectFilters {
  const page = Number(params.get('page'));
  const status = params.get('status');
  const phase = params.get('phase');
  const riskLevel = params.get('riskLevel');
  const sortBy = params.get('sortBy');
  const sortOrder = params.get('sortOrder');

  return {
    ...DEFAULT_PROJECT_FILTERS,
    search: params.get('search')?.trim() ?? '',
    status: included(PROJECT_STATUSES, status) ? (status as ProjectStatus) : '',
    phase: included(PROJECT_PHASES, phase) ? (phase as ProjectPhase) : '',
    riskLevel: included(PROJECT_RISK_LEVELS, riskLevel) ? (riskLevel as ProjectRiskLevel) : '',
    sector: params.get('sector')?.trim() ?? '',
    projectId: params.get('projectId')?.trim() ?? '',
    regionId: params.get('regionId')?.trim() ?? '',
    cohort: params.get('cohort')?.trim() || undefined,
    page: Number.isInteger(page) && page > 0 ? page : 1,
    sortBy: included(SORT_FIELDS, sortBy) ? (sortBy as ProjectSortField) : 'updatedAt',
    sortOrder: sortOrder === 'asc' || sortOrder === 'desc' ? (sortOrder as SortOrder) : 'desc',
  };
}

export function projectViewFromParams(params: URLSearchParams): ProjectView {
  return params.get('view') === 'kanban'
    ? 'kanban'
    : params.get('view') === 'table'
      ? 'table'
      : 'dashboard';
}

export function updateProjectParam(
  current: URLSearchParams,
  key: (typeof PROJECT_FILTER_KEYS)[number],
  value: string | number,
): URLSearchParams {
  const next = new URLSearchParams(current);
  const stringValue = String(value).trim();
  const isDefault =
    (key === 'page' && stringValue === '1') ||
    (key === 'sortBy' && stringValue === DEFAULT_PROJECT_FILTERS.sortBy) ||
    (key === 'sortOrder' && stringValue === DEFAULT_PROJECT_FILTERS.sortOrder) ||
    (key === 'view' && stringValue === 'dashboard');

  if (!stringValue || isDefault) next.delete(key);
  else next.set(key, stringValue);

  if (key !== 'page') next.delete('page');
  return next;
}

export function clearProjectParams(current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const key of PROJECT_FILTER_KEYS) next.delete(key);
  return next;
}

export function hasProjectFilters(filters: ProjectFilters): boolean {
  return Boolean(
    filters.search ||
      filters.status ||
      filters.phase ||
      filters.riskLevel ||
      filters.sector ||
      filters.projectId ||
      filters.regionId ||
      filters.cohort ||
      filters.page > 1 ||
      filters.sortBy !== DEFAULT_PROJECT_FILTERS.sortBy ||
      filters.sortOrder !== DEFAULT_PROJECT_FILTERS.sortOrder,
  );
}

export function projectPortfolioFilters(filters: ProjectFilters): ProjectPortfolioFilters {
  return {
    search: filters.search,
    status: filters.status,
    phase: filters.phase,
    riskLevel: filters.riskLevel,
    sector: filters.sector,
    projectId: filters.projectId,
    regionId: filters.regionId,
    cohort: filters.cohort,
  };
}

export function projectCohortLabel(cohort: string): string {
  if (cohort === 'health:UNAVAILABLE') return 'No recorded health model';
  const separator = cohort.indexOf(':');
  const kind = cohort.slice(0, separator);
  const value = cohort.slice(separator + 1);
  if (kind === 'sector') {
    try {
      const sector: unknown = JSON.parse(value);
      return typeof sector === 'string' ? `Sector: ${sector}` : 'Unspecified sector';
    } catch {
      return 'Selected record cohort';
    }
  }
  return `${kind === 'health' ? 'Model' : kind === 'risk' ? 'Recorded risk' : 'Status'}: ${value.toLowerCase().replaceAll('_', ' ')}`;
}
